import { useCallback, useEffect, useState } from 'react';
import {
  FieldErrors,
  UseFormGetValues,
  UseFormSetError,
} from 'react-hook-form';
import {
  BaseellaStatus,
  BaseTransaction,
  PermissionEnum,
  ResponseError,
  responseErrorsResolver,
  SelectItemProps,
  TransactionWarning,
  TransactionWarningActionBody,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  useApplyIncomingTransactionWarningActionMutation,
  useCheckIncomingTransactionMutation,
  useCreateIncomingTransactionMutation,
  useLazyGetIncomingListQuery,
  useLazyGetIncomingTransactionAmlByIdQuery,
  useLazyGetIncomingTransactionByIdQuery,
  useLazyGetIncomingTransactionWarningsQuery,
  useLazyGetIncomingTransactionWarningQuery,
  useUpdateIncomingTransactionMutation,
  useResetIncomingTransactionWarningActionMutation,
  useLazyGetIncomingTransactionWarningsInfoQuery,
} from '../store/services';
import StatusesUrl from '../../../types/statuses-url';
import useRetry from '../../outgoing/helpers/use-retry';
import { setWarnings } from '../store/slices/transaction';
import { useLazyGetStatusesTransactionByIdQuery } from '../../../store/statuses/services';
import {
  CheckTransaction,
  CheckTransactionStatus,
} from '../../../types/check-transaction';
import { UpdateIncomingTransaction } from '../types';

const useRequests = ({
  transactionId,
  warnings,
  transaction,
  activePermissions,
  setCheckError,
  fields,
  setError,
  setErrorMessage,
  setStatusChangeError,
  getValues,
  errors,
  isDirty,
  recipientAccounts,
  panelIsOpen,
  setStatusCheckError,
  currencyId,
  sum,
  paymentType,
  setWarningActionError,
}: {
  transactionId?: string;
  warnings?: TransactionWarning[];
  transaction?: BaseTransaction;
  activePermissions?: PermissionEnum[];
  setCheckError: (data: string) => void;
  fields: any;
  setError: UseFormSetError<any>;
  setErrorMessage: (data: string) => void;
  setStatusChangeError: (data: string) => void;
  getValues: UseFormGetValues<any>;
  errors: FieldErrors<any>;
  isDirty?: boolean;
  recipientAccounts?: SelectItemProps[];
  panelIsOpen: boolean;
  setStatusCheckError: (data: string) => void;
  currencyId?: string;
  sum?: string;
  paymentType?: string;
  setWarningActionError: (
    data: { warningId: string; error: string } | undefined,
  ) => void;
}) => {
  const dispatch = useAppDispatch();

  const [feeValue, setFeeValue] = useState<number | undefined>(undefined);
  const [checkStatus, setCheckStatus] = useState<
    CheckTransactionStatus | undefined
  >(undefined);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [selectedWarningId, setSelectedWarningId] = useState('');
  const [transferDateWithTime, setTransferDateWithTime] = useState(false);
  const [accpetedInWithTime, setAcceptedInWithTime] = useState(false);
  const [postingDateWithTime, setPostingDateWithTime] = useState(false);

  const [getIncomingTransactionById, { isLoading, isUninitialized }] =
    useLazyGetIncomingTransactionByIdQuery();
  const [update, { isLoading: editIsLoading, isError: updateIsError }] =
    useUpdateIncomingTransactionMutation();
  const [create, { isSuccess: createSuccess, isLoading: createInProgress }] =
    useCreateIncomingTransactionMutation();
  const [getTransactionsList] = useLazyGetIncomingListQuery();
  const [
    getIncomingTransactionWarnings,
    { isLoading: isWarningLoading, isFetching: isWarningFetching },
  ] = useLazyGetIncomingTransactionWarningsQuery();
  const [
    getIncomingTransactionWarningsInfo,
    { isLoading: isWarningInfoLoading },
  ] = useLazyGetIncomingTransactionWarningsInfoQuery();
  const [
    getIncomingTransactionWarningById,
    { isLoading: isWarningByIdLoading, isFetching: isWarningByIdFetching },
  ] = useLazyGetIncomingTransactionWarningQuery();
  const [checkPayment, { isLoading: checkInProgress }] =
    useCheckIncomingTransactionMutation();
  const [getIncomingTransactionByIdAml, { isLoading: isLoadingAml }] =
    useLazyGetIncomingTransactionAmlByIdQuery();
  const [
    applyIncomingTransactionWarning,
    { isLoading: applyTransactionsWarningIsLoading },
  ] = useApplyIncomingTransactionWarningActionMutation();
  const [
    resetIncomingTransactionWarning,
    {
      isLoading: resetTransactionsWarningIsLoading,
      isSuccess: resetTransactionsWarningIsSuccess,
    },
  ] = useResetIncomingTransactionWarningActionMutation();

  const [
    getStatusesTransactionById,
    {
      isLoading: isLoadingStatuses,
      isFetching: isFetchingStatuses,
      isUninitialized: isUninitializedStatuses,
    },
  ] = useLazyGetStatusesTransactionByIdQuery();

  const getIncomingTransactionWarningByIdAction = async ({
    id,
    warningId,
  }: {
    id: string;
    warningId: string;
  }) => {
    setSelectedWarningId(warningId);

    await getIncomingTransactionWarningById({
      id,
      warningId,
    });
  };

  const onResetTransactionWarningAction = async (warningId: string) => {
    const response = (await resetIncomingTransactionWarning({
      warningId,
      transactionId: transactionId as string,
    })) as { error: ResponseError; data?: { data: TransactionWarning } };

    if (response && !response.error) {
      const nextWarnings = warnings?.map((item) => {
        const result = { ...item };

        if (result.id === response?.data?.data?.id) {
          result.action = response.data.data.action;
          result.matchStatus = response.data.data.matchStatus;
        }

        return result;
      });

      getIncomingTransactionWarningsInfo(transactionId as string);

      dispatch(setWarnings(nextWarnings as TransactionWarning[]));
    }
  };

  const onApplyTransactionAction = async ({
    body,
    warningId,
  }: {
    body: TransactionWarningActionBody;
    warningId: string;
  }) => {
    setWarningActionError(undefined);

    const response = (await applyIncomingTransactionWarning({
      transactionId: transactionId as string,
      body,
      warningId,
    })) as { error: ResponseError; data?: { data: TransactionWarning } };

    if (response && !response.error) {
      const nextWarnings = warnings
        ?.map((item) => {
          const result = { ...item };

          if (result.id === response?.data?.data?.id) {
            result.action = response.data.data.action;
            result.matchStatus = response.data.data.matchStatus;
          }

          return result;
        })
        ?.filter((item) => !item.action);

      getIncomingTransactionWarningsInfo(transactionId as string);

      if (!nextWarnings?.length) {
        getStatusesTransactionById({
          id: transactionId as string,
          url: StatusesUrl.INCOMING,
          baseellaStatusFrom: transaction?.latestQueuedStatuses
            ?.baseellaStatus as string,
        });
      }
    } else if (response?.error) {
      setWarningActionError({
        warningId,
        error: response?.error?.data?.message as string,
      });
    }
  };

  const { getTransaction, onClearTimer, internalError } = useRetry({
    getTransactionFunction: getIncomingTransactionById,
    id: transactionId as string,
    isRetry: true,
    statusUrl: StatusesUrl.INCOMING,
    getStatusesFunction: getStatusesTransactionById,
    getTransactionWarningsFunction: getIncomingTransactionWarnings,
    getTransactionWarningsInfoFunction: getIncomingTransactionWarningsInfo,
    chatPermission: activePermissions?.includes(PermissionEnum.CHANNELS_LIST),
    statusPermission: activePermissions?.includes(
      PermissionEnum.TRANSACTIONS_INCOMING_EDIT_ACTION,
    ),
    warningsInfoPermission: activePermissions?.includes(
      PermissionEnum.TRANSACTIONS_INCOMING_AML_LIST,
    ),
  });

  const abortController = (() => {
    let controller = new AbortController();
    let controllerSignal = controller.signal;

    const abortPendingRequest = () => {
      controller.abort();
      // eslint-disable-next-line react-hooks/immutability
      controller = new AbortController();
      controllerSignal = controller.signal;
    };

    const getSignalToken = () => controllerSignal;

    return {
      abortPendingRequest,
      getSignalToken,
    };
  })();

  const checkPaymentAction = useCallback(async () => {
    abortController.abortPendingRequest();

    setCheckError('');
    setStatusCheckError('');

    const body: CheckTransaction = {
      paymentType: fields.paymentType,
      sum: Number(fields.sum),
      senderAccountNumber: fields.senderAccountNumber,
      recipientAccountNumber: fields.recipientAccountNumber || '',
      currencyId: fields.currencyId,
      info: fields.info,
    };

    if (fields?.caspId) {
      body.caspId = fields.caspId;
    }

    const response = (await checkPayment({ body, abortController })) as {
      data?: any;
      error?: { data: { message?: string }; message?: string } | ResponseError;
    };

    if ('data' in response) {
      setFeeValue(response.data.data.totalFee);
      setCheckStatus(response.data.data.status);

      if (response?.data?.data?.errorMessage) {
        setStatusCheckError(response?.data?.data?.errorMessage);
      }

      if (response.data.data.status === CheckTransactionStatus.GREEN) {
        setSubmitDisabled(false);
      }
    } else {
      setSubmitDisabled(true);

      if (response && response.error) {
        responseErrorsResolver({
          errors: response.error as ResponseError,
          setError,
          setGlobalError: setCheckError,
        });
      }
    }
  }, [
    checkPayment,
    fields.paymentType,
    fields.sum,
    fields.senderAccountNumber,
    fields.recipientAccountNumber,
    fields.currencyId,
    fields.caspId,
    fields.info,
  ]);

  const onSubmit = async () => {
    let response;

    if (!Object.keys(errors).length) {
      setStatusChangeError('');
      setErrorMessage('');
      const values = getValues();

      const body: UpdateIncomingTransaction = {
        paymentType: values.paymentType || undefined,
        pspPartnerId: values.pspPartnerId || undefined,
        pspPartnerAccountNumber: values.pspPartnerAccountNumber || undefined,
        documentNumber: values.documentNumber || undefined,
        info: values.info || undefined,
      };

      if (values.acceptedIn) {
        const date = new Date(values.acceptedIn);
        if (accpetedInWithTime) {
          body.acceptedIn = date.toUTCString();
        } else {
          body.acceptedIn = values.acceptedIn;
        }
      }

      if (values?.depositDateOfDestinationAccount) {
        const date = new Date(values.depositDateOfDestinationAccount);
        if (postingDateWithTime) {
          body.depositDateOfDestinationAccount = date.toUTCString();
        } else {
          const currentDate = new Date();
          const hours = currentDate.getHours();
          const minutes = currentDate.getMinutes();
          const seconds = currentDate.getSeconds();

          date.setHours(hours);
          date.setMinutes(minutes);
          date.setSeconds(seconds);
          body.depositDateOfDestinationAccount = date.toISOString();
        }
      }

      if (transaction?.baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS) {
        body.recipientAccountNumber = values.recipientAccountNumber;

        if (values.pspIntegrationAccountId) {
          body.pspIntegrationAccountId = values.pspIntegrationAccountId;
        } else if (
          !values.pspIntegrationAccountId &&
          transaction?.pspIntegrationAccountId
        ) {
          body.pspIntegrationAccountId = null;
        }
      }

      if (transaction?.baseellaStatus === BaseellaStatus.MISSING_PAYER) {
        body.remitterFullName = values.remitterFullName;
        body.remitterCountry = values.remitterCountry;
        body.remitterCity = values.remitterCity;
        body.remitterAddress = values.remitterAddress;
      }

      response = (await update({
        body,
        id: transactionId as string,
      })) as {
        data?: BaseTransaction;
        error: ResponseError;
      };

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError: setErrorMessage,
        });
      } else {
        getStatusesTransactionById({
          id: transactionId as string,
          url: StatusesUrl.INCOMING,
          baseellaStatusFrom: transaction?.latestQueuedStatuses
            ?.baseellaStatus as string,
        });
      }
    }

    return response;
  };

  const getIncomingTransactionByIdAmlAction = () => {
    getIncomingTransactionByIdAml(transactionId as string);
  };

  useEffect(() => {
    if (
      !transactionId &&
      isDirty &&
      fields.paymentType &&
      fields.paymentType === 'CASH_OVER_COUNTER' &&
      fields.sum &&
      fields.currencyId &&
      fields.remitterFullName &&
      fields.collectorContact &&
      fields.info
    ) {
      checkPaymentAction();
    }
  }, [
    isDirty,
    transaction,
    fields.paymentType,
    fields.sum,
    fields.currencyId,
    fields.remitterFullName,
    fields.collectorContact,
    fields.info,
  ]);

  useEffect(() => {
    if (
      !transactionId &&
      isDirty &&
      fields.paymentType &&
      fields.sum.toString() &&
      fields.senderAccountNumber &&
      fields.recipientAccountNumber &&
      fields.currencyId &&
      fields.info
    ) {
      if (fields.paymentType !== 'CRYPTO_ASSET_PAYMENT') {
        checkPaymentAction();
      } else if (fields.paymentType === 'CRYPTO_ASSET_PAYMENT') {
        if (fields?.caspId) {
          checkPaymentAction();
        }
      }
    }
  }, [
    transactionId,
    isDirty,
    checkPaymentAction,
    fields.paymentType,
    fields.sum,
    fields.senderAccountNumber,
    fields.recipientAccountNumber,
    fields.currencyId,
    fields?.caspId,
    fields.info,
  ]);

  useEffect(() => {
    if (
      transactionId &&
      transaction?.baseellaStatus === BaseellaStatus.UNDEFINED_FUNDS &&
      recipientAccounts?.length &&
      recipientAccounts?.find(
        (item) => item.value === fields.recipientAccountNumber,
      ) &&
      !recipientAccounts?.find(
        (item) => item.value === transaction.recipientAccountNumber,
      ) &&
      currencyId &&
      paymentType
    ) {
      checkPaymentAction();
    }
  }, [
    transactionId,
    transaction?.baseellaStatus,
    recipientAccounts,
    fields.recipientAccountNumber,
    checkPaymentAction,
    currencyId,
    sum,
  ]);

  useEffect(() => {
    if (panelIsOpen && transactionId) {
      getTransaction();

      setAcceptedInWithTime(true);
      setTransferDateWithTime(true);
      setPostingDateWithTime(true);
    }
  }, [panelIsOpen, transactionId]);

  return {
    feeValue,
    setFeeValue,
    checkStatus,
    setCheckStatus,
    submitDisabled,
    setSubmitDisabled,
    isLoading,
    isUninitialized,
    editIsLoading,
    updateIsError,
    create,
    createSuccess,
    createInProgress,
    getTransactionsList,
    isWarningLoading,
    isWarningFetching,
    checkInProgress,
    isLoadingAml,
    applyTransactionsWarningIsLoading,
    isLoadingStatuses,
    isFetchingStatuses,
    isUninitializedStatuses,
    onApplyTransactionAction,
    onClearTimer,
    internalError,
    onSubmit,
    getIncomingTransactionByIdAmlAction,
    getStatusesTransactionById,
    getTransaction,
    onResetTransactionWarningAction,
    resetTransactionsWarningIsLoading,
    resetTransactionsWarningIsSuccess,
    getIncomingTransactionWarningByIdAction,
    isWarningByIdLoading,
    isWarningByIdFetching,
    isWarningInfoLoading,
    selectedWarningId,
    transferDateWithTime,
    setTransferDateWithTime,
    accpetedInWithTime,
    setAcceptedInWithTime,
    postingDateWithTime,
    setPostingDateWithTime,
  };
};

export default useRequests;
